import { Component, Inject, ViewEncapsulation, OnDestroy } from '@angular/core';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../../services/infrastructure/config.service';
import { SliderModelService } from '../../../services/infrastructure/sliderModel.service';
import { ConfirmDialogComponent } from './../../../common/components/confirm-dialog/confirm-dialog.component';
import { BroadcastService } from './../../../services/infrastructure/broadcaster.service';
import { FormGroup, FormBuilder } from '@angular/forms';
import { OtiService } from '../../../services/oti/oti.service';
import { LoaderService } from '../../../services/infrastructure/loader.service';
import { ChangeDetectorRef, OnInit } from '@angular/core';

@Component({
  selector: 'app-oti-advanced-search',
  templateUrl: './oti-advanced-search.component.html',
  styleUrls: ['./oti-advanced-search.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OtiAdvancedSearchComponent implements OnDestroy {
  searchForm: FormGroup;
  searchType: 'CPP' | 'SEED' = 'CPP';

  // Dropdown options
  accountOptions: DropdownOption[] = [];
  orderTypeOptions: DropdownOption[] = [];
  vendorOptions: DropdownOption[] = [];
  productNameOptions: DropdownOption[] = [];

  // Selected values for multi-select dropdowns
  selectedAccounts: string[] = [];
  selectedOrderTypes: string[] = [];
  selectedVendors: string[] = [];
  selectedProductNames: string[] = [];
  disableAdvancedSearchButton: boolean = true;
  maxOrderDate = new Date();
  maxRequestDeliveryDate = new Date();
  // Dropdown states
  dropdownStates = {
    account: false,
    orderType: false,
    vendor: false,
    productName: false
  };
  private subscription = new Array<Subscription>();

  constructor(
    public dialogRef: MatDialogRef<OtiAdvancedSearchComponent>,
    private otiService: OtiService,
    private loaderService: LoaderService,
    @Inject(MAT_DIALOG_DATA) public data: SearchData,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder
  ) {
    this.searchType = data.searchType || 'CPP';
    this.initializeForm();
    this.buildDropdownOptions();  }

  ngOnInit(): void {

    this.subscription.push(
      this.searchForm.valueChanges.subscribe(() => {
        this.updateSearchButtonState();
      })
    );

    this.updateSearchButtonState();
    // Close dropdowns when clicking outside
    document.addEventListener('click', this.closeDropdownsOnOutsideClick.bind(this));
  }

  ngOnDestroy(): void {
    this.subscription.forEach(subscription => subscription.unsubscribe());
    document.removeEventListener('click', this.closeDropdownsOnOutsideClick.bind(this));
  }
  private updateSearchButtonState(): void {
    const formValue = this.searchForm.value;

    const hasFormValue = Object.values(formValue).some(value =>
      value !== null && value !== undefined && value !== ''
    );

    const hasMultiSelectValue =
      this.selectedAccounts.length > 0 ||
      this.selectedOrderTypes.length > 0 ||
      this.selectedVendors.length > 0 ||
      this.selectedProductNames.length > 0;

    this.disableAdvancedSearchButton = !(hasFormValue || hasMultiSelectValue);
  }
  private initializeForm(): void {
    this.searchForm = this.fb.group({
      webOrderNumber: [''],
      orderNumber: [''],
      customerPO: [''],
      shipmentNumber: [''],
      orderDateFrom: [''],
      orderDateTo: [''],
      deliveryDateFrom: [''],
      deliveryDateTo: ['']
    });
  }

  private buildDropdownOptions(): void {
    this.loaderService.setLoaderStatus(true);

    this.subscription.push(this.otiService.getOTIOrderFilterData(this.searchType).subscribe((result: any) => {
      if (result) {

        const data = result; // Use masterDataList array

        // Build Account options - using AccountList from API
        if (data.accountList && data.accountList.length > 0) {
          this.accountOptions = data.accountList.map(acc => ({
            value: acc.id,
            label: acc.value
          }));
        }

        // Build Order Type options - using OrderTypeList from API
        if (data.orderTypeList && data.orderTypeList.length > 0) {
          this.orderTypeOptions = data.orderTypeList.map(type => ({
            value: type.id,
            label: type.value
          }));
        }

        // Build Vendor options - using VendorList from API
        if (data.vendorList && data.vendorList.length > 0) {
          this.vendorOptions = data.vendorList.map(vendor => ({
            value: vendor.id,
            label: vendor.value
          }));
        }

        // Build Product Name options - using ProductDataList from API
        if (data.productDataList && data.productDataList.length > 0) {
          this.productNameOptions = data.productDataList.map(product => ({
            value: product.id,
            label: product.value
          }));
        }

        // Initialize with "Select ALL" by default
        this.selectAllByDefault();
        if (this.data?.searchCriteria) {
          this.loadExistingCriteria();
        }
        this.updateSearchButtonState();
        this.loaderService.setLoaderStatus(false);
        this.cdr.detectChanges();

      }
      else {
        this.loaderService.setLoaderStatus(false);
      }
    }, error => {
      this.loaderService.setLoaderStatus(false);
    }));
  }

  private selectAllByDefault(): void {
    this.selectedAccounts = this.accountOptions.map(option => option.value);
    this.selectedOrderTypes = this.orderTypeOptions.map(option => option.value);
    this.selectedVendors = this.vendorOptions.map(option => option.value);
    this.selectedProductNames = this.productNameOptions.map(option => option.value);
  }

  private loadExistingCriteria(): void {
    const criteria = this.data.searchCriteria;

    // Patch form values for text inputs and dates
    this.searchForm.patchValue({
      webOrderNumber: criteria.webOrderNumbers ? criteria.webOrderNumbers.join(',') : '',
      orderNumber: criteria.orderNumbers ? criteria.orderNumbers.join(',') : '',
      customerPO: criteria.customerPOs ? criteria.customerPOs.join(',') : '',
      shipmentNumber: criteria.shipmentNumbers ? criteria.shipmentNumbers.join(',') : '',
      orderDateFrom: criteria.orderDateFrom ? new Date(criteria.orderDateFrom) : null,
      orderDateTo: criteria.orderDateTo ? new Date(criteria.orderDateTo) : null,
      deliveryDateFrom: criteria.deliveryDateFrom ? new Date(criteria.deliveryDateFrom) : null,
      deliveryDateTo: criteria.deliveryDateTo ? new Date(criteria.deliveryDateTo) : null
    });

    // Load multi-select values
    if (criteria.accounts) this.selectedAccounts = criteria.accounts;
    if (criteria.orderTypes) this.selectedOrderTypes = criteria.orderTypes;
    if (criteria.vendors) this.selectedVendors = criteria.vendors;
    if (criteria.productNames) this.selectedProductNames = criteria.productNames;
  }

  // AC4 & AC5: Auto-fill "To" date when "From" date is entered
  onOrderDateFromChange(): void {
    const fromDate = this.searchForm.get('orderDateFrom')?.value;
    const toDate = this.searchForm.get('orderDateTo')?.value;
    if (fromDate) {
      if (!toDate) {
        // if ToDate not selected, set it same as FromDate
        this.searchForm.get('orderDateTo')?.setValue(fromDate);
      } else if (toDate < fromDate) {
        // if ToDate exists but is earlier than FromDate, adjust it
        this.searchForm.get('orderDateTo')?.setValue(fromDate);
      }
    }
  }

  onDeliveryDateFromChange(): void {
    const fromDate = this.searchForm.get('deliveryDateFrom')?.value;
    const toDate = this.searchForm.get('deliveryDateTo')?.value;
    if (fromDate) {
      if (toDate && toDate < fromDate) {
        // if ToDate exists but is earlier than FromDate, adjust it
        this.searchForm.get('deliveryDateTo')?.setValue(fromDate);
      }
    }
  }
  // Handle Order Date "To" field changes
  onOrderDateToChange(): void {
    const fromDate = this.searchForm.get('orderDateFrom')?.value;
    const toDate = this.searchForm.get('orderDateTo')?.value;

    if (toDate && !fromDate) {
      // If ToDate is selected but FromDate is empty, set FromDate same as ToDate
      this.searchForm.get('orderDateFrom')?.setValue(toDate);
    }
  }

  // Handle Delivery Date "To" field changes
  onDeliveryDateToChange(): void {
    const fromDate = this.searchForm.get('deliveryDateFrom')?.value;
    const toDate = this.searchForm.get('deliveryDateTo')?.value;

    if (toDate && !fromDate) {
      // If ToDate is selected but FromDate is empty, set FromDate same as ToDate
      this.searchForm.get('deliveryDateFrom')?.setValue(toDate);
    }
  }

  // Multi-select dropdown functionality
  toggleDropdown(dropdown: string): void {
    Object.keys(this.dropdownStates).forEach(key => {
      this.dropdownStates[key as keyof typeof this.dropdownStates] = key === dropdown ?
        !this.dropdownStates[key as keyof typeof this.dropdownStates] : false;
    });
  }

  toggleSelectAll(dropdown: string, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    switch (dropdown) {
      case 'account':
        this.selectedAccounts = isChecked ? this.accountOptions.map(opt => opt.value) : [];
        break;
      case 'orderType':
        this.selectedOrderTypes = isChecked ? this.orderTypeOptions.map(opt => opt.value) : [];
        break;
      case 'vendor':
        this.selectedVendors = isChecked ? this.vendorOptions.map(opt => opt.value) : [];
        break;
      case 'productName':
        this.selectedProductNames = isChecked ? this.productNameOptions.map(opt => opt.value) : [];
        break;
    }
    this.updateSearchButtonState();
  }

  toggleOption(dropdown: string, value: string, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;

    switch (dropdown) {
      case 'account':
        this.updateSelectedArray(this.selectedAccounts, value, isChecked);
        break;
      case 'orderType':
        this.updateSelectedArray(this.selectedOrderTypes, value, isChecked);
        break;
      case 'vendor':
        this.updateSelectedArray(this.selectedVendors, value, isChecked);
        break;
      case 'productName':
        this.updateSelectedArray(this.selectedProductNames, value, isChecked);
        break;
    }
    this.updateSearchButtonState();
  }

  private updateSelectedArray(array: string[], value: string, isChecked: boolean): void {
    if (isChecked && !array.includes(value)) {
      array.push(value);
    } else if (!isChecked && array.includes(value)) {
      const index = array.indexOf(value);
      array.splice(index, 1);
    }
  }

  isAllSelected(dropdown: string): boolean {
    switch (dropdown) {
      case 'account':
        return this.selectedAccounts.length === this.accountOptions.length;
      case 'orderType':
        return this.selectedOrderTypes.length === this.orderTypeOptions.length;
      case 'vendor':
        return this.selectedVendors.length === this.vendorOptions.length;
      case 'productName':
        return this.selectedProductNames.length === this.productNameOptions.length;
      default:
        return false;
    }
  }

  getSelectedText(dropdown: string): string {
    let selectedArray: string[] = [];
    let totalCount = 0;
    let optionsArray: DropdownOption[] = [];

    switch (dropdown) {
      case 'account':
        selectedArray = this.selectedAccounts;
        totalCount = this.accountOptions.length;
        optionsArray = this.accountOptions;
        break;
      case 'orderType':
        selectedArray = this.selectedOrderTypes;
        totalCount = this.orderTypeOptions.length;
        optionsArray = this.orderTypeOptions;

        break;
      case 'vendor':
        selectedArray = this.selectedVendors;
        totalCount = this.vendorOptions.length;
        optionsArray = this.vendorOptions;

        break;
      case 'productName':
        selectedArray = this.selectedProductNames;
        totalCount = this.productNameOptions.length;
        optionsArray = this.productNameOptions;

        break;
    }

    if (selectedArray.length === 0) {
      const friendlyName = dropdown
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
      return `Select ${friendlyName}`;
    } else if (selectedArray.length === totalCount) {
      return 'All Selected';
    } else if (selectedArray.length === 1) {
      const selectedOption = optionsArray.find(option => option.value === selectedArray[0]);
      return selectedOption ? selectedOption.label : selectedArray[0];
    } else if (selectedArray.length <= 2) {
      // Show up to 2 account names
      const selectedLabels = selectedArray.map(value => {
        const option = optionsArray.find(opt => opt.value === value);
        return option ? option.label : value;
      });
      return selectedLabels.join(', ');
    } else {
      return `${selectedArray.length} selected`;
    }
  }

  private closeDropdownsOnOutsideClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.multi-select-wrapper')) {
      Object.keys(this.dropdownStates).forEach(key => {
        this.dropdownStates[key as keyof typeof this.dropdownStates] = false;
      });
    }
  }

  private parseCsvList(raw: string | null): string[] {
    if (!raw) return [];
    return raw
      .split(',')
      .map(item => item.trim())
      .filter(item => !!item);
  }

  onAdvancedSearch(): void {
    const fromDate = this.searchForm.get('deliveryDateFrom')?.value;
    const toDate = this.searchForm.get('deliveryDateTo')?.value;

    if (!toDate && fromDate) {
      // set ToDate if ToDate is blank and FromDate is selected
      this.searchForm.get('deliveryDateTo')?.setValue(fromDate);
    }
    const formValue = this.searchForm.value;

    // Parse CSV fields into arrays
    const webOrderNumbers = this.parseCsvList(formValue.webOrderNumber);
    const orderNumbers = this.parseCsvList(formValue.orderNumber);
    const customerPOs = this.parseCsvList(formValue.customerPO);
    const shipmentNumbers = this.parseCsvList(formValue.shipmentNumber);

    const searchCriteria: any = {
      ...formValue,
      webOrderNumbers,
      orderNumbers,
      customerPOs,
      shipmentNumbers,
      accounts: this.selectedAccounts,
      accountNames: this.accountOptions.filter(opt => this.selectedAccounts.includes(opt.value)).map(opt => opt.label),
      orderTypes: this.selectedOrderTypes,
      orderTypeNames: this.orderTypeOptions.filter(opt => this.selectedOrderTypes.includes(opt.value)).map(opt => opt.label),
      vendors: this.selectedVendors,
      vendorNames: this.vendorOptions.filter(opt => this.selectedVendors.includes(opt.value)).map(opt => opt.label),
      productNames: this.selectedProductNames,
      productLabels: this.productNameOptions.filter(opt => this.selectedProductNames.includes(opt.value)).map(opt => opt.label),
      searchType: this.searchType
    };

    // Remove raw CSV strings
    delete searchCriteria.webOrderNumber;
    delete searchCriteria.orderNumber;
    delete searchCriteria.customerPO;
    delete searchCriteria.shipmentNumber;
    // Remove empty arrays/values
    Object.keys(searchCriteria).forEach(key => {
      const val = searchCriteria[key];
      if (
        val == null ||
        (Array.isArray(val) && val.length === 0) ||
        (typeof val === 'string' && val.trim() === '')
      ) {
        delete searchCriteria[key];
      }
    });
    this.dialogRef.close({ action: 'search', searchCriteria });
  }

  onCancel(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  onClose(): void {
    this.dialogRef.close({ action: 'cancel' });
  }

  onClearAll(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    // Reset form controls
    this.searchForm.reset();

    // Deselect all in multi-selects
    this.selectedAccounts = [];
    this.selectedOrderTypes = [];
    this.selectedVendors = [];
    this.selectedProductNames = [];

    // Update button state
    this.updateSearchButtonState();

    // Close all dropdowns
    Object.keys(this.dropdownStates).forEach(key => {
      this.dropdownStates[key as keyof typeof this.dropdownStates] = false;
    });
  }
}

export interface SearchData {
  searchCriteria?: any;
  searchResults?: any[];
}
export interface DropdownOption {
  value: string;
  label: string;
}

export interface SearchData {
  searchType: 'CPP' | 'SEED';
  existingData?: any[];
  searchCriteria?: any;
}
